import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api, getErrorMessage } from "@/lib/seoApi";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CodeCard from "@/components/CodeCard";
import { Bot, Search, AlertTriangle, ShieldCheck, ShieldX, Wand2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_CLS = {
  ai_training: "bg-violet-50 text-violet-700 border-violet-200",
  ai_search: "bg-sky-50 text-sky-700 border-sky-200",
  ai_assistant: "bg-teal-50 text-teal-700 border-teal-200",
  search_engine: "bg-slate-100 text-slate-700 border-slate-200",
};

const T = {
  en: {
    category: { ai_training: "AI training", ai_search: "AI search", ai_assistant: "AI assistant", search_engine: "Search engine" },
    title: "Robots.txt AI Bot Checker",
    sub: "See exactly which AI crawlers can access your site - and generate a robots.txt that matches your AI policy.",
    tabCheck: "Check a website",
    tabGenerate: "Generate robots.txt",
    checkPh: "example.com",
    check: "Check access",
    checking: "Checking...",
    errTitle: "Check failed",
    errEmpty: "Please enter a domain to check",
    found: "Found",
    notFound: "Not found",
    aiAllowed: "AI bots allowed",
    canRead: "Can read and cite your content",
    aiBlocked: "AI bots blocked",
    cannotRead: "Cannot access your pages",
    accessFor: (d) => `Bot access for ${d}`,
    all: (n) => `All (${n})`,
    allowed: "Allowed",
    blocked: "Blocked",
    thBot: "Bot",
    thCategory: "Category",
    thStatus: "Status",
    thRule: "Matched rule",
    viewRaw: "View raw robots.txt",
    sitemaps: (n) => ` (${n} sitemap(s) declared)`,
    emptyBody: "Enter a domain to check access for 20 crawlers including GPTBot, ClaudeBot, PerplexityBot, Google-Extended and more.",
    policyTitle: "Choose your AI crawler policy",
    presetAllowAll: "Allow all",
    presetRecommended: "Recommended",
    presetBlockAI: "Block all AI",
    presetHint: "Recommended = allow AI search & assistants (they cite you) but block pure training crawlers.",
    presetApplied: "Preset applied",
    thAllow: "Allow",
    fSitemap: "Sitemap URL (optional)",
    fDisallow: "Paths to block for ALL crawlers (one per line, optional)",
    generate: "Generate robots.txt",
    generating: "Generating...",
    generated: "robots.txt generated",
    botsFailed: "Failed to load bot list",
    genEmpty: "Toggle the crawlers you want to allow or block, then generate a ready-to-upload robots.txt. Place it at the root of your site.",
  },
  fr: {
    category: { ai_training: "Entraînement IA", ai_search: "Recherche IA", ai_assistant: "Assistant IA", search_engine: "Moteur de recherche" },
    title: "Vérificateur robots.txt des robots IA",
    sub: "Voyez exactement quels robots IA peuvent accéder à votre site - et générez un robots.txt qui reflète votre politique IA.",
    tabCheck: "Vérifier un site",
    tabGenerate: "Générer robots.txt",
    checkPh: "exemple.com",
    check: "Vérifier l'accès",
    checking: "Vérification...",
    errTitle: "Échec de la vérification",
    errEmpty: "Veuillez entrer un domaine à vérifier",
    found: "Trouvé",
    notFound: "Introuvable",
    aiAllowed: "Robots IA autorisés",
    canRead: "Peuvent lire et citer votre contenu",
    aiBlocked: "Robots IA bloqués",
    cannotRead: "Ne peuvent pas accéder à vos pages",
    accessFor: (d) => `Accès des robots pour ${d}`,
    all: (n) => `Tous (${n})`,
    allowed: "Autorisés",
    blocked: "Bloqués",
    thBot: "Robot",
    thCategory: "Catégorie",
    thStatus: "Statut",
    thRule: "Règle appliquée",
    viewRaw: "Voir le robots.txt brut",
    sitemaps: (n) => ` (${n} sitemap(s) déclaré(s))`,
    emptyBody: "Entrez un domaine pour vérifier l'accès de 20 robots dont GPTBot, ClaudeBot, PerplexityBot, Google-Extended et plus.",
    policyTitle: "Choisissez votre politique de robots IA",
    presetAllowAll: "Tout autoriser",
    presetRecommended: "Recommandé",
    presetBlockAI: "Bloquer toute IA",
    presetHint: "Recommandé = autoriser la recherche et les assistants IA (ils vous citent) mais bloquer les robots d'entraînement pur.",
    presetApplied: "Préréglage appliqué",
    thAllow: "Autoriser",
    fSitemap: "URL du sitemap (optionnel)",
    fDisallow: "Chemins à bloquer pour TOUS les robots (un par ligne, optionnel)",
    generate: "Générer robots.txt",
    generating: "Génération...",
    generated: "robots.txt généré",
    botsFailed: "Impossible de charger la liste des robots",
    genEmpty: "Activez ou bloquez les robots voulus, puis générez un robots.txt prêt à téléverser. Placez-le à la racine de votre site.",
  },
};

export default function RobotsChecker() {
  const { lang } = useLang();
  const t = T[lang];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div className="max-w-2xl">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-2 text-muted-foreground">{t.sub}</p>
      </div>

      <Tabs defaultValue="check" className="mt-8">
        <TabsList>
          <TabsTrigger value="check" data-testid="robots-tab-check">{t.tabCheck}</TabsTrigger>
          <TabsTrigger value="generate" data-testid="robots-tab-generate">{t.tabGenerate}</TabsTrigger>
        </TabsList>
        <TabsContent value="check" className="mt-6">
          <CheckerTab t={t} />
        </TabsContent>
        <TabsContent value="generate" className="mt-6">
          <GeneratorTab t={t} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const CheckerTab = ({ t }) => {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState("all");

  const check = async (e) => {
    e?.preventDefault();
    if (!domain.trim()) {
      setError(t.errEmpty);
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/analyze/robots", { url: domain.trim() });
      setResult(data);
      setFilter("all");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const filteredBots = useMemo(() => {
    if (!result) return [];
    if (filter === "allowed") return result.bots.filter((b) => b.allowed);
    if (filter === "blocked") return result.bots.filter((b) => !b.allowed);
    return result.bots;
  }, [result, filter]);

  return (
    <div>
      <form onSubmit={check} className="flex flex-col sm:flex-row gap-3 max-w-2xl">
        <Input
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder={t.checkPh}
          className="h-11 bg-card"
          data-testid="robots-ai-checker-domain-input"
        />
        <Button type="submit" size="lg" disabled={loading} className="h-11 shrink-0" data-testid="robots-ai-checker-run-button">
          <Search className="h-4 w-4 mr-2" />
          {loading ? t.checking : t.check}
        </Button>
      </form>

      {error && (
        <Alert variant="destructive" className="mt-6 max-w-2xl" data-testid="robots-ai-checker-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t.errTitle}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="mt-8 space-y-4" data-testid="robots-ai-checker-loading">
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
          </div>
          <Skeleton className="h-72 rounded-lg" />
        </div>
      )}

      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="mt-8 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground">robots.txt</p>
                <p className="mt-1 text-lg font-heading font-semibold" data-testid="robots-summary-found">
                  {result.found ? t.found : t.notFound}
                </p>
                <p className="text-xs text-muted-foreground mt-1 break-all">{result.robots_url}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground">{t.aiAllowed}</p>
                <p className="mt-1 text-lg font-heading font-semibold text-emerald-600 flex items-center gap-2" data-testid="robots-summary-allowed">
                  <ShieldCheck className="h-5 w-5" />
                  {result.summary.allowed_ai} / {result.summary.total_ai}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{t.canRead}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground">{t.aiBlocked}</p>
                <p className="mt-1 text-lg font-heading font-semibold text-red-600 flex items-center gap-2" data-testid="robots-summary-blocked">
                  <ShieldX className="h-5 w-5" />
                  {result.summary.blocked_ai} / {result.summary.total_ai}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{t.cannotRead}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-base font-heading">{t.accessFor(result.domain)}</CardTitle>
              <Tabs value={filter} onValueChange={setFilter}>
                <TabsList className="h-8">
                  <TabsTrigger value="all" className="text-xs px-3" data-testid="robots-filter-all">{t.all(result.bots.length)}</TabsTrigger>
                  <TabsTrigger value="allowed" className="text-xs px-3" data-testid="robots-filter-allowed">{t.allowed}</TabsTrigger>
                  <TabsTrigger value="blocked" className="text-xs px-3" data-testid="robots-filter-blocked">{t.blocked}</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent className="px-0">
              <div className="max-h-[560px] overflow-auto">
                <Table data-testid="robots-ai-checker-results-table">
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead className="pl-6">{t.thBot}</TableHead>
                      <TableHead>{t.thCategory}</TableHead>
                      <TableHead>{t.thStatus}</TableHead>
                      <TableHead className="pr-6">{t.thRule}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBots.map((bot) => (
                      <TableRow key={bot.name} className="hover:bg-muted/40" data-testid={`robots-bot-row-${bot.name}`}>
                        <TableCell className="pl-6">
                          <p className="font-medium text-sm">{bot.name}</p>
                          <p className="text-xs text-muted-foreground">{bot.company} - {bot.purpose}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${CATEGORY_CLS[bot.category]} text-[10px] whitespace-nowrap`}>{t.category[bot.category]}</Badge>
                        </TableCell>
                        <TableCell>
                          {bot.allowed ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{t.allowed}</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{t.blocked}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="pr-6 text-xs text-muted-foreground max-w-[260px]">
                          <span className="line-clamp-2">{bot.reason}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {result.found && (
            <Accordion type="single" collapsible>
              <AccordionItem value="raw" className="border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-sm hover:no-underline" data-testid="robots-raw-accordion-trigger">
                  {t.viewRaw}{result.sitemaps.length > 0 && t.sitemaps(result.sitemaps.length)}
                </AccordionTrigger>
                <AccordionContent>
                  <pre className="code-scroll font-mono text-xs leading-6 p-4 rounded-md overflow-auto max-h-[400px] whitespace-pre-wrap break-words" data-testid="robots-raw-content">
                    {result.raw}
                  </pre>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </motion.div>
      )}

      {!result && !loading && !error && (
        <div className="mt-12 rounded-lg border border-dashed p-10 text-center max-w-2xl" data-testid="robots-checker-empty-state">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Bot className="h-6 w-6" />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{t.emptyBody}</p>
        </div>
      )}
    </div>
  );
};

const GeneratorTab = ({ t }) => {
  const [bots, setBots] = useState([]);
  const [botsLoading, setBotsLoading] = useState(true);
  const [allowed, setAllowed] = useState({});
  const [sitemap, setSitemap] = useState("");
  const [disallowPaths, setDisallowPaths] = useState("");
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/bots")
      .then(({ data }) => {
        setBots(data.bots);
        setAllowed(Object.fromEntries(data.bots.map((b) => [b.user_agent, true])));
      })
      .catch(() => toast.error(t.botsFailed))
      .finally(() => setBotsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (preset) => {
    const next = {};
    bots.forEach((b) => {
      if (preset === "allow_all") next[b.user_agent] = true;
      else if (preset === "block_all_ai") next[b.user_agent] = b.category === "search_engine";
      else if (preset === "recommended") next[b.user_agent] = b.category !== "ai_training";
    });
    setAllowed(next);
    toast.success(t.presetApplied);
  };

  const generate = async () => {
    setLoading(true);
    try {
      const policies = {};
      bots.forEach((b) => {
        if (!allowed[b.user_agent]) policies[b.user_agent] = "block";
      });
      const { data } = await api.post("/generate/robots", {
        policies,
        default_disallow: disallowPaths.split("\n").map((p) => p.trim()).filter(Boolean),
        sitemap: sitemap.trim() || null,
      });
      setOutput(data.content);
      toast.success(t.generated);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <Card className="lg:col-span-6 h-fit">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">{t.policyTitle}</CardTitle>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => applyPreset("allow_all")} data-testid="robots-preset-allow-all-button">{t.presetAllowAll}</Button>
            <Button variant="outline" size="sm" onClick={() => applyPreset("recommended")} data-testid="robots-preset-recommended-button">{t.presetRecommended}</Button>
            <Button variant="outline" size="sm" onClick={() => applyPreset("block_all_ai")} data-testid="robots-preset-block-all-button">{t.presetBlockAI}</Button>
          </div>
          <p className="text-xs text-muted-foreground pt-1">{t.presetHint}</p>
        </CardHeader>
        <CardContent className="px-0">
          {botsLoading ? (
            <div className="px-6 space-y-2">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-md" />)}
            </div>
          ) : (
            <div className="max-h-[520px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="pl-6">{t.thBot}</TableHead>
                    <TableHead>{t.thCategory}</TableHead>
                    <TableHead className="pr-6 text-right">{t.thAllow}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bots.map((bot) => (
                    <TableRow key={bot.user_agent} className="hover:bg-muted/40">
                      <TableCell className="pl-6">
                        <p className="font-medium text-sm">{bot.name}</p>
                        <p className="text-xs text-muted-foreground">{bot.company}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${CATEGORY_CLS[bot.category]} text-[10px] whitespace-nowrap`}>{t.category[bot.category]}</Badge>
                      </TableCell>
                      <TableCell className="pr-6 text-right">
                        <Switch
                          checked={!!allowed[bot.user_agent]}
                          onCheckedChange={(v) => setAllowed((a) => ({ ...a, [bot.user_agent]: v }))}
                          aria-label={`Allow ${bot.name}`}
                          data-testid={`robots-toggle-${bot.user_agent}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="px-6 pt-4 space-y-4 border-t mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="rg-sitemap">{t.fSitemap}</Label>
              <Input id="rg-sitemap" value={sitemap} onChange={(e) => setSitemap(e.target.value)} placeholder="https://exemple.com/sitemap.xml" data-testid="robots-generator-sitemap-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rg-disallow">{t.fDisallow}</Label>
              <textarea
                id="rg-disallow"
                value={disallowPaths}
                onChange={(e) => setDisallowPaths(e.target.value)}
                placeholder={"/admin\n/private"}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="robots-generator-disallow-input"
              />
            </div>
            <Button onClick={generate} disabled={loading || botsLoading} className="w-full" data-testid="robots-generator-generate-button">
              <Wand2 className="h-4 w-4 mr-2" />
              {loading ? t.generating : t.generate}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="lg:col-span-6">
        {output ? (
          <CodeCard
            title="robots.txt"
            language="robots.txt"
            code={output}
            filename="robots.txt"
            testIdPrefix="robots-generator-output"
            maxHeight="640px"
          />
        ) : (
          <div className="rounded-lg border border-dashed p-10 text-center h-full flex flex-col items-center justify-center min-h-[300px]" data-testid="robots-generator-empty-state">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <Bot className="h-6 w-6" />
            </div>
            <p className="mt-3 text-sm text-muted-foreground max-w-sm">{t.genEmpty}</p>
          </div>
        )}
      </div>
    </div>
  );
};
